import {
    getSessionDeviceType,
    getSessionReferrerSource,
    shouldSkipAnalyticsPath,
} from '@/lib/analytics-context';
import { trackAppwriteEvent } from '@/lib/appwrite-analytics';
import { trackEventFn } from '@/server/functions/analytics';
import { locale } from '@/lib/appwrite-web';

const VISITOR_KEY = 'an_vid';
const GEO_KEY = 'an_geo';

/** Event names written by the public site. */
export const AnalyticsEvent = {
    pageView: 'page_view',
    articleView: 'article_view',
    search: 'search',
    newsletterSubscribe: 'newsletter_subscribe',
    adClick: 'ad_click',
    adImpression: 'ad_impression',
} as const;

export type AnalyticsEventName =
    (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

export type VisitorGeo = {
    countryCode: string | null;
    country: string | null;
    continentCode: string | null;
    continent: string | null;
};

function isBrowser() {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

/** Stable anonymous visitor id (localStorage). */
export function getVisitorId(): string | null {
    if (!isBrowser()) return null;
    try {
        let id = localStorage.getItem(VISITOR_KEY);
        if (!id || id.length < 8 || id.length > 36) {
            id = crypto.randomUUID();
            localStorage.setItem(VISITOR_KEY, id);
        }
        return id;
    } catch {
        return null;
    }
}

function articleIdFromPath(pathname: string): string | null {
    const match = pathname.match(/^\/article\/([^/]+)\/?$/);
    if (!match?.[1]) return null;
    try {
        return decodeURIComponent(match[1]);
    } catch {
        return match[1];
    }
}

function readCachedGeo(): VisitorGeo | null {
    if (!isBrowser()) return null;
    try {
        const raw = sessionStorage.getItem(GEO_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as VisitorGeo;
        if (!parsed || typeof parsed !== 'object') return null;
        return {
            countryCode: parsed.countryCode ?? null,
            country: parsed.country ?? null,
            continentCode: parsed.continentCode ?? null,
            continent: parsed.continent ?? null,
        };
    } catch {
        return null;
    }
}

function writeCachedGeo(geo: VisitorGeo) {
    try {
        sessionStorage.setItem(GEO_KEY, JSON.stringify(geo));
    } catch {
        // ignore
    }
}

let geoPromise: Promise<VisitorGeo | null> | null = null;

/**
 * Resolve visitor geo via Appwrite Locale (IP geolocation by DB-IP).
 * Called from the browser so Appwrite sees the visitor's IP.
 * Result is cached for the tab session.
 */
export function resolveVisitorGeo(): Promise<VisitorGeo | null> {
    if (!isBrowser()) return Promise.resolve(null);
    const cached = readCachedGeo();
    if (cached?.countryCode || cached?.continentCode) {
        return Promise.resolve(cached);
    }
    if (geoPromise) return geoPromise;

    geoPromise = (async () => {
        try {
            const result = await locale.get();
            const geo: VisitorGeo = {
                countryCode: result.countryCode?.trim().slice(0, 2) || null,
                country: result.country?.trim().slice(0, 100) || null,
                continentCode: result.continentCode?.trim().slice(0, 2) || null,
                continent: result.continent?.trim().slice(0, 64) || null,
            };
            writeCachedGeo(geo);
            return geo;
        } catch {
            return null;
        } finally {
            // Allow retry next navigation if this attempt failed with no cache.
            if (!readCachedGeo()?.countryCode) {
                geoPromise = null;
            }
        }
    })();

    return geoPromise;
}

export type TrackEventInput = {
    name: AnalyticsEventName | string;
    path?: string;
    articleId?: string | null;
    meta?: Record<string, unknown>;
};

/**
 * Fire-and-forget analytics event. Never throws to callers.
 * Dedupes identical page/article views within a short window.
 * Attaches Appwrite Locale geo when available.
 */
export function trackEvent(input: TrackEventInput) {
    if (!isBrowser()) return;
    const visitorId = getVisitorId();
    if (!visitorId) return;

    const path = input.path ?? window.location.pathname;
    if (shouldSkipAnalyticsPath(path)) return;

    const articleId =
        input.articleId === undefined
            ? articleIdFromPath(path)
            : input.articleId;

    const metaAdId =
        typeof input.meta?.adId === 'string' ? input.meta.adId : '';
    const metaSlotId =
        typeof input.meta?.slotId === 'string' ? input.meta.slotId : '';
    // Include ad/slot ids so concurrent banner events are not collapsed.
    const dedupeKey = `${input.name}|${path}|${articleId ?? ''}|${metaAdId}|${metaSlotId}`;
    try {
        const last = sessionStorage.getItem(`an_last:${dedupeKey}`);
        if (last && Date.now() - Number(last) < 4_000) return;
        sessionStorage.setItem(`an_last:${dedupeKey}`, String(Date.now()));
    } catch {
        // ignore storage failures
    }

    // Auto pageviews already cover `page_view`. Named events carry the extra props.
    if (input.name !== AnalyticsEvent.pageView) {
        const props: Record<string, unknown> = { path };
        if (articleId) props.articleId = articleId;
        if (input.meta) {
            for (const [key, value] of Object.entries(input.meta)) {
                if (value == null) continue;
                props[key] = value;
            }
        }
        trackAppwriteEvent(input.name, props, path);
    }

    void (async () => {
        const geo = (await resolveVisitorGeo()) ?? {
            countryCode: null,
            country: null,
            continentCode: null,
            continent: null,
        };

        await trackEventFn({
            data: {
                visitorId,
                name: input.name,
                path,
                articleId: articleId || null,
                meta: input.meta ?? null,
                countryCode: geo.countryCode,
                country: geo.country,
                continentCode: geo.continentCode,
                continent: geo.continent,
                deviceType: getSessionDeviceType(),
                referrerSource: getSessionReferrerSource(),
            },
        });
    })().catch(() => {
        // Analytics must never break the site.
    });
}

export function trackPageView(pathname: string, search = '') {
    if (shouldSkipAnalyticsPath(pathname)) return;
    // Warm Locale lookup early so the first beacon can include geo.
    void resolveVisitorGeo();
    const articleId = articleIdFromPath(pathname);
    const path = search ? `${pathname}${search}` : pathname;
    if (articleId) {
        trackEvent({
            name: AnalyticsEvent.articleView,
            path: pathname,
            articleId,
        });
        return;
    }
    if (pathname.startsWith('/search')) {
        trackEvent({
            name: AnalyticsEvent.search,
            path,
        });
        return;
    }
    trackEvent({
        name: AnalyticsEvent.pageView,
        path: pathname,
    });
}

export function trackNewsletterSubscribe() {
    trackEvent({ name: AnalyticsEvent.newsletterSubscribe });
}

export function trackAdClick(
    adId: string,
    placement?: string,
    extras?: { slotId?: string; size?: string },
) {
    trackEvent({
        name: AnalyticsEvent.adClick,
        meta: {
            adId,
            ...(placement ? { placement } : {}),
            ...(extras?.slotId ? { slotId: extras.slotId } : {}),
            ...(extras?.size ? { size: extras.size } : {}),
        },
    });
}

/** Fired once when an ad unit becomes viewable on the page. */
export function trackAdImpression(
    adId: string,
    extras?: { placement?: string; slotId?: string; size?: string },
) {
    trackEvent({
        name: AnalyticsEvent.adImpression,
        meta: {
            adId,
            ...(extras?.placement ? { placement: extras.placement } : {}),
            ...(extras?.slotId ? { slotId: extras.slotId } : {}),
            ...(extras?.size ? { size: extras.size } : {}),
        },
    });
}
